import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Clock,
  Star,
  StarOff,
  Eye,
  ChevronLeft,
  ChevronRight,
  Edit,
  X,
  Trash2,
  Search,
  Upload,
  Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { resolveImageUrl } from '@/utils/imageUrl';
import { OptimizedImage } from '@/components/OptimizedImage';

const API = `${window.location.origin}/api`;

const AdminBusinesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1 });
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [editForm, setEditForm] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await fetch(`${API}/upload/image`, {
        method: 'POST',
        credentials: 'include',
        body: uploadData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setEditForm(prev => ({ ...prev, image_url: data.url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const fetchBusinesses = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString() });
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API}/admin/businesses?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data.businesses);
        setPagination({ page: data.page, pages: data.pages });
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, [statusFilter, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const updateStatus = async (businessId, status, reason = null) => {
    try {
      const body = { status };
      if (reason) body.rejection_reason = reason;

      const response = await fetch(`${API}/admin/businesses/${businessId}/verification`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(`Business ${status}`);
        fetchBusinesses(pagination.page);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const toggleFeatured = async (businessId, isFeatured) => {
    try {
      const response = await fetch(`${API}/admin/businesses/${businessId}/featured`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_featured: !isFeatured })
      });

      if (response.ok) {
        toast.success(isFeatured ? 'Removed from featured' : 'Added to featured');
        fetchBusinesses(pagination.page);
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const toggleBusinessFeature = async (businessId, feature, currentValue) => {
    try {
      const response = await fetch(`${API}/admin/businesses/${businessId}/feature`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [feature]: !currentValue })
      });

      if (response.ok) {
        toast.success(`${feature.replace('_', ' ')} ${!currentValue ? 'enabled' : 'disabled'}`);
        fetchBusinesses(pagination.page);
      } else {
        toast.error('Failed to update feature');
      }
    } catch (error) {
      console.error('Error toggling business feature:', error);
      toast.error('Failed to update feature');
    }
  };

  const handleReject = () => {
    if (selectedBusiness) {
      updateStatus(selectedBusiness.business_id, 'rejected', rejectionReason);
      setRejectDialogOpen(false);
      setSelectedBusiness(null);
      setRejectionReason('');
    }
  };

  const handleDelete = async () => {
    if (selectedBusiness) {
      try {
        const response = await fetch(`${API}/admin/businesses/${selectedBusiness.business_id}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (response.ok) {
          toast.success('Business deleted successfully');
          fetchBusinesses(pagination.page);
        } else {
          toast.error('Failed to delete business');
        }
      } catch (error) {
        toast.error('Failed to delete business');
      } finally {
        setDeleteDialogOpen(false);
        setSelectedBusiness(null);
      }
    }
  };

  const openEditDialog = (business) => {
    setSelectedBusiness(business);
    setEditForm({
      name: business.name || '',
      description: business.description || '',
      category: business.category || '',
      phone: business.phone || '',
      email: business.email || '',
      website: business.website || '',
      address: business.address || '',
      city: business.city || '',
      state: business.state || '',
      zip_code: business.zip_code || '',
      is_online_only: business.is_online_only || false,
      service_area: business.service_area || '',
      image_url: business.image_url || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!selectedBusiness) return;

    try {
      const response = await fetch(`${API}/admin/businesses/${selectedBusiness.business_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast.success('Business updated successfully');
        fetchBusinesses(pagination.page);
        setEditDialogOpen(false);
        setSelectedBusiness(null);
      } else {
        toast.error('Failed to update business');
      }
    } catch (error) {
      toast.error('Failed to update business');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return <span className="verified-badge"><CheckCircle className="w-3 h-3" />Verified</span>;
      case 'pending':
        return <span className="pending-badge"><Clock className="w-3 h-3" />Pending</span>;
      case 'rejected':
        return <span className="rejected-badge"><XCircle className="w-3 h-3" />Rejected</span>;
      default:
        return null;
    }
  };

  const categories = [
    'Restaurant', 'Retail', 'Beauty & Wellness', 'Professional Services',
    'Health & Medical', 'Entertainment', 'Education', 'Technology',
    'Automotive', 'Arts & Culture', 'Real Estate & Housing', 'Travel & Lodging',
    'Media & Creative Agencies', 'Community & Non-Profit', 'Home & Construction',
    'Manufacturing & Wholesale', 'Other'
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Manage</p>
          <h1 className="text-3xl font-bold text-white tracking-tight">Businesses</h1>
        </div>

        <div className="flex gap-2">
          {['', 'pending', 'verified', 'rejected'].map((status) => (
            <button
              key={status || 'all'}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status
                  ? 'bg-white text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
              data-testid={`filter-${status || 'all'}`}
            >
              {status || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, category, address, or description..."
            className="w-full pl-10 pr-10 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700"
            data-testid="admin-business-search"
          />
          {searchInput && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <button
          type="submit"
          className="px-6 py-3 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
        >
          Search
        </button>
      </form>

      {searchQuery && (
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <span>Showing results for &quot;{searchQuery}&quot;</span>
          <button onClick={clearSearch} className="text-purple-400 hover:underline">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 skeleton rounded-lg" />
          ))}
        </div>
      ) : businesses.length > 0 ? (
        <>
          <div className="space-y-4">
            {businesses.map((business) => (
              <motion.div
                key={business.business_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="glass-card rounded-xl p-6"
                data-testid={`admin-business-${business.business_id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{business.name}</h3>
                      {getStatusBadge(business.verification_status)}
                      {business.is_online_only && (
                        <span className="px-2 py-1 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs">
                          Online
                        </span>
                      )}
                      {business.is_featured && (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 text-xs">
                          <Star className="w-3 h-3" />
                          Featured
                        </span>
                      )}
                    </div>
                    <p className="text-zinc-500 text-sm mb-1">{business.category}</p>
                    <p className="text-zinc-600 text-sm">{business.address || business.service_area || 'No address'}</p>
                    {business.rejection_reason && (
                      <p className="text-red-400 text-sm mt-2">Reason: {business.rejection_reason}</p>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {business.verification_status === 'pending' && (
                      <>
                        <button
                          onClick={() => updateStatus(business.business_id, 'verified')}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
                          data-testid={`approve-${business.business_id}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setSelectedBusiness(business);
                            setRejectDialogOpen(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                          data-testid={`reject-${business.business_id}`}
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </>
                    )}

                    {business.verification_status === 'verified' && (
                      <button
                        onClick={() => toggleFeatured(business.business_id, business.is_featured)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          business.is_featured
                            ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                        data-testid={`feature-${business.business_id}`}
                      >
                        {business.is_featured ? (
                          <>
                            <StarOff className="w-4 h-4" />
                            Unfeature
                          </>
                        ) : (
                          <>
                            <Star className="w-4 h-4" />
                            Feature
                          </>
                        )}
                      </button>
                    )}

                    <button
                      onClick={() => openEditDialog(business)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                      data-testid={`edit-${business.business_id}`}
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>

                    <Link
                      to={`/businesses/${business.business_id}`}
                      target="_blank"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 text-zinc-400 hover:bg-zinc-700 transition-colors"
                      data-testid={`view-${business.business_id}`}
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </Link>

                    <button
                      onClick={() => {
                        setSelectedBusiness(business);
                        setDeleteDialogOpen(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                      data-testid={`delete-${business.business_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>

                  {/* Feature Toggles for Verified & Claimed Businesses */}
                  {business.verification_status === 'verified' && business.is_claimed && (
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-zinc-800">
                      <span className="text-zinc-500 text-xs uppercase tracking-wider">Selling:</span>
                      <button
                        onClick={() => toggleBusinessFeature(business.business_id, 'marketplace_enabled', business.marketplace_enabled)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                          business.marketplace_enabled !== false
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                            : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                        }`}
                        data-testid={`toggle-marketplace-${business.business_id}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${business.marketplace_enabled !== false ? 'bg-green-400' : 'bg-zinc-600'}`} />
                        {business.marketplace_enabled !== false ? 'Enabled' : 'Disabled'}
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => fetchBusinesses(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 font-mono text-sm text-zinc-400">
                {pagination.page} / {pagination.pages}
              </span>
              <button
                onClick={() => fetchBusinesses(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20">
          <p className="text-zinc-500">No businesses found.</p>
        </div>
      )}

      {/* Reject Dialog */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Reject Business</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Provide a reason for rejection (optional but recommended).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter rejection reason..."
            className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
            data-testid="rejection-reason-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-reject-button"
            >
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Business</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete &quot;{selectedBusiness?.name}&quot;? This will also delete all associated reviews, locations, and favorites. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <AlertDialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Edit Business</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Update business information
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Business Name</label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Category</label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-white"
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Description</label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-white"
                rows={3}
              />
            </div>

            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Business Image</label>
              
              {/* Image Preview */}
              {editForm.image_url && (
                <div className="relative mb-3">
                  <OptimizedImage 
                    src={editForm.image_url}
                    alt="Preview" 
                    preset="card"
                    className="w-full h-40 object-cover rounded-lg border border-zinc-800"
                  />
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, image_url: '' })}
                    className="absolute top-2 right-2 w-8 h-8 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {/* Upload Button */}
              <div className="flex gap-2 mb-2">
                <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-zinc-700 hover:border-zinc-600 bg-zinc-900/50 cursor-pointer transition-colors">
                  {uploadingImage ? (
                    <>
                      <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" />
                      <span className="text-zinc-400 text-sm">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-zinc-400" />
                      <span className="text-zinc-400 text-sm">Upload Image</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                </label>
              </div>
              
              {/* URL Input */}
              <div className="flex items-center gap-2">
                <span className="text-zinc-600 text-xs">or paste URL:</span>
                <Input
                  value={editForm.image_url}
                  onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="flex-1 bg-zinc-900 border-zinc-800 text-white text-sm h-9"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Phone</label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Email</label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>
            </div>

            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Website</label>
              <Input
                value={editForm.website}
                onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                className="bg-zinc-900 border-zinc-800 text-white"
              />
            </div>

            <div className="flex items-center gap-2 p-3 rounded-lg bg-zinc-900 border border-zinc-800">
              <input
                type="checkbox"
                id="is_online_only"
                checked={editForm.is_online_only}
                onChange={(e) => setEditForm({ ...editForm, is_online_only: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="is_online_only" className="text-zinc-300">Online-only business (no physical location)</label>
            </div>

            {editForm.is_online_only ? (
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Service Area</label>
                <Input
                  value={editForm.service_area}
                  onChange={(e) => setEditForm({ ...editForm, service_area: e.target.value })}
                  placeholder="e.g., Global, USA, California"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="text-zinc-400 text-sm mb-1 block">Address</label>
                  <Input
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-zinc-400 text-sm mb-1 block">City</label>
                    <Input
                      value={editForm.city}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                      className="bg-zinc-900 border-zinc-800 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm mb-1 block">State</label>
                    <Input
                      value={editForm.state}
                      onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                      className="bg-zinc-900 border-zinc-800 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm mb-1 block">ZIP Code</label>
                    <Input
                      value={editForm.zip_code}
                      onChange={(e) => setEditForm({ ...editForm, zip_code: e.target.value })}
                      className="bg-zinc-900 border-zinc-800 text-white"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEditSave}
              className="bg-blue-600 text-white hover:bg-blue-700"
              data-testid="confirm-edit-button"
            >
              Save Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


export { AdminBusinesses };
export default AdminBusinesses;
